/**
 * Javascript script port of:
 * http://www.dasplankton.de/downloads/VisionDefectColor.as
 * http://contrast-a.dasplankton.com/
 *
 * Original action script src (at time of writing) included alongside this javascript file
 */
export default class VisionDefectColor {
    lms2rgb = [];
    rgb2lms = [];
    gammaRGB = [];
    inflection;
    deficiency;
    a1;
    b1;
    c1;
    a2;
    b2;
    c2;

    constructor() {
        this.rgb2lms[0] = 0.05059983;
        this.rgb2lms[1] = 0.08585369;
        this.rgb2lms[2] = 0.0095242;

        this.rgb2lms[3] = 0.01893033;
        this.rgb2lms[4] = 0.08925308;
        this.rgb2lms[5] = 0.01370054;

        this.rgb2lms[6] = 0.00292202;
        this.rgb2lms[7] = 0.00975732;
        this.rgb2lms[8] = 0.07145979;

        this.lms2rgb[0] = 30.830854;
        this.lms2rgb[1] = -29.832659;
        this.lms2rgb[2] = 1.610474;

        this.lms2rgb[3] = -6.481468;
        this.lms2rgb[4] = 17.715578;
        this.lms2rgb[5] = -2.532642;

        this.lms2rgb[6] = -0.37569;
        this.lms2rgb[7] = -1.199062;
        this.lms2rgb[8] = 14.273846;

        this.gammaRGB[0] = 2.1;
        this.gammaRGB[1] = 2.0;
        this.gammaRGB[2] = 2.1;
    }

    convertDefect(myDeficiency, originalColor) {
        this.deficiency = myDeficiency;
        const originalColorRGB = VisionDefectColor.hex24torgb(originalColor);
        let red = originalColorRGB.red;
        let green = originalColorRGB.green;
        let blue = originalColorRGB.blue;
        let tmp;

        // Remove gamma to linearize RGB intensities
        red = Math.pow(red, 1.0 / this.gammaRGB[0]);
        green = Math.pow(green, 1.0 / this.gammaRGB[1]);
        blue = Math.pow(blue, 1.0 / this.gammaRGB[2]);

        // Convert to LMS (dot product with transform matrix)
        let redOld = red;
        let greenOld = green;

        red =
            redOld * this.rgb2lms[0] +
            greenOld * this.rgb2lms[1] +
            blue * this.rgb2lms[2];
        green =
            redOld * this.rgb2lms[3] +
            greenOld * this.rgb2lms[4] +
            blue * this.rgb2lms[5];
        blue =
            redOld * this.rgb2lms[6] +
            greenOld * this.rgb2lms[7] +
            blue * this.rgb2lms[8];

        switch (this.deficiency) {
            case 'normalVision':
                break;
            case 'deuteranopeVision':
                this.setupDefect();
                tmp = blue / red;
                // See which side of the inflection line we fall...
                tmp < this.inflection
                    ? (green = -(this.a1 * red + this.c1 * blue) / this.b1)
                    : (green = -(this.a2 * red + this.c2 * blue) / this.b2);
                break;
            case 'protanopeVision':
                this.setupDefect();
                tmp = blue / green;
                // See which side of the inflection line we fall...
                tmp < this.inflection
                    ? (red = -(this.b1 * green + this.c1 * blue) / this.a1)
                    : (red = -(this.b2 * green + this.c2 * blue) / this.a2);
                break;
            case 'tritanopeVision':
                this.setupDefect();
                tmp = green / red;
                // See which side of the inflection line we fall...
                tmp < this.inflection
                    ? (blue = -(this.a1 * red + this.b1 * green) / this.c1)
                    : (blue = -(this.a2 * red + this.b2 * green) / this.c2);
                break;
            case 'colorBlindnessVision':
                var gray = this.clamp(
                    0.3 * originalColorRGB.red +
                        0.59 * originalColorRGB.green +
                        0.11 * originalColorRGB.blue,
                    0,
                    255
                );
                red = gray;
                green = gray;
                blue = gray;
                return this.colorToHexString(
                    VisionDefectColor.rgbToHex24(red, green, blue)
                ); // no other transformations!
            default:
                break;
        }

        // Convert back to RGB (cross product with transform matrix)
        redOld = red;
        greenOld = green;

        red =
            redOld * this.lms2rgb[0] +
            greenOld * this.lms2rgb[1] +
            blue * this.lms2rgb[2];
        green =
            redOld * this.lms2rgb[3] +
            greenOld * this.lms2rgb[4] +
            blue * this.lms2rgb[5];
        blue =
            redOld * this.lms2rgb[6] +
            greenOld * this.lms2rgb[7] +
            blue * this.lms2rgb[8];

        // Apply gamma to go back to non-linear intensities
        red = Math.pow(red, this.gammaRGB[0]);
        green = Math.pow(green, this.gammaRGB[1]);
        blue = Math.pow(blue, this.gammaRGB[2]);

        // Ensure that we stay within the RGB gamut
        // *** FIX THIS: it would be better to desaturate than blindly clip.
        red = this.clamp(red, 0.0, 255.0);
        green = this.clamp(green, 0.0, 255.0);
        blue = this.clamp(blue, 0.0, 255.0);

        return this.colorToHexString(
            VisionDefectColor.rgbToHex24(red, green, blue)
        );
    }

    // This sets "inflection"
    setupDefect() {
        const anchor_e = [];
        const anchor = [];

        /*
        Load the LMS anchor-point values for lambda = 475 & 485 nm (for
        protans & deutans) and the LMS values for lambda = 575 & 660 nm
        (for tritans)
        */
        anchor[0] = 0.08008;
        anchor[1] = 0.1579;
        anchor[2] = 0.5897;
        anchor[3] = 0.1284;
        anchor[4] = 0.2237;
        anchor[5] = 0.3636;
        anchor[6] = 0.9856;
        anchor[7] = 0.7325;
        anchor[8] = 0.001079;
        anchor[9] = 0.0914;
        anchor[10] = 0.007009;
        anchor[11] = 0.0;

        /* We also need LMS for RGB=(1,1,1)- the equal-energy point (one of
         * our anchors) (we can just peel this out of the rgb2lms transform
         * matrix)
         */
        anchor_e[0] = this.rgb2lms[0] + this.rgb2lms[1] + this.rgb2lms[2];
        anchor_e[1] = this.rgb2lms[3] + this.rgb2lms[4] + this.rgb2lms[5];
        anchor_e[2] = this.rgb2lms[6] + this.rgb2lms[7] + this.rgb2lms[8];

        switch (this.deficiency) {
            case 'deuteranopeVision':
                // find a,b,c for lam=575nm and lam=475
                this.a1 = anchor_e[1] * anchor[8] - anchor_e[2] * anchor[7];
                this.b1 = anchor_e[2] * anchor[6] - anchor_e[0] * anchor[8];
                this.c1 = anchor_e[0] * anchor[7] - anchor_e[1] * anchor[6];
                this.a2 = anchor_e[1] * anchor[2] - anchor_e[2] * anchor[1];
                this.b2 = anchor_e[2] * anchor[0] - anchor_e[0] * anchor[2];
                this.c2 = anchor_e[0] * anchor[1] - anchor_e[1] * anchor[0];
                // Inflection set here
                this.inflection = anchor_e[2] / anchor_e[0];
                break;
            case 'protanopeVision':
                // find a,b,c for lam=575nm and lam=475
                this.a1 = anchor_e[1] * anchor[8] - anchor_e[2] * anchor[7];
                this.b1 = anchor_e[2] * anchor[6] - anchor_e[0] * anchor[8];
                this.c1 = anchor_e[0] * anchor[7] - anchor_e[1] * anchor[6];
                this.a2 = anchor_e[1] * anchor[2] - anchor_e[2] * anchor[1];
                this.b2 = anchor_e[2] * anchor[0] - anchor_e[0] * anchor[2];
                this.c2 = anchor_e[0] * anchor[1] - anchor_e[1] * anchor[0];
                // Inflection set here
                this.inflection = anchor_e[2] / anchor_e[1];
                break;
            case 'tritanopeVision':
                // Set 1: regions where lambda_a=575, set 2: lambda_a=475
                this.a1 = anchor_e[1] * anchor[11] - anchor_e[2] * anchor[10];
                this.b1 = anchor_e[2] * anchor[9] - anchor_e[0] * anchor[11];
                this.c1 = anchor_e[0] * anchor[10] - anchor_e[1] * anchor[9];
                this.a2 = anchor_e[1] * anchor[5] - anchor_e[2] * anchor[4];
                this.b2 = anchor_e[2] * anchor[3] - anchor_e[0] * anchor[5];
                this.c2 = anchor_e[0] * anchor[4] - anchor_e[1] * anchor[3];
                // Inflection set here
                this.inflection = anchor_e[1] / anchor_e[0];
                break;
            default:
                break;
        }
    }

    clamp(n, low, high) {
        let ret = 0;
        n > high ? (ret = high) : n < low ? (ret = low) : (ret = n);
        return ret;
    }

    static rgbToHex24(red, green, blue) {
        return (red << 16) | (green << 8) | blue;
    }

    static hex24torgb(color) {
        const red = (color >> 16) & 0xff;
        const green = (color >> 8) & 0xff;
        const blue = color & 0xff;

        return {
            red,
            green,
            blue,
        };
    }

    colorToHexString(color) {
        let colorString = color.toString(16);
        const missingCharacterCount = 6 - colorString.length;
        const missingCharacters = '0'.repeat(missingCharacterCount);

        return `0x${missingCharacters}${colorString.toUpperCase()}`;
    }
}
